import { Navigate, useLocation, type Location } from "react-router-dom";
import { Suspense } from "react";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { useUserContext } from "@/context/UserContext";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  getDefaultClientPathAfterAuth,
  hasClientCreatedProfile,
  isClientOnboardingComplete,
  needsFlexDemo,
  type UserWithCompany,
} from "@/shared/constants/clientOnboarding.const";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { AuthRouteFallback } from "@/routes/RouteFallback";
import { SectorGuestView } from "@/routes/SectorGuestView";
import { isPublicSectorOverviewPath } from "@/routes/sectorPublicRoutes.util";
import { isCollaborationWalletPath } from "@/shared/constants/collaborationWallet.const";

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT" | "FACILITATOR" | "SUB_ADMIN" | "ECOSYSTEM_HEAD" | "EXPERT" | "MANAGER";
  exp: number;
  iat: number;
};
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

/** No hooks — safe to return early on every branch (avoids Rules of Hooks issues with redirects). */
function ProtectedRouteGate({
  isAuthenticated,
  isLoading,
  userDetails,
  location,
  children,
}: {
  isAuthenticated: boolean;
  isLoading: boolean;
  userDetails: UserWithCompany | null | undefined;
  location: Location;
  children: React.ReactNode;
}) {
  const token = Cookies.get("token");
  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (decoded.role === "ADMIN" || decoded.role === "SUB_ADMIN") {
        return <Navigate to="/admin/companies" replace />;
      }
      if (decoded.role === "FACILITATOR") {
        return <Navigate to="/facilitator/dashboard" replace />;
      }
      if (isClusterPortalRole(decoded.role)) {
        return <Navigate to="/ecosystem-head/dashboard" replace />;
      }
      if (decoded.role === "EXPERT") {
        return <Navigate to="/expert/dashboard" replace />;
      }
      // MANAGER stays in company ProtectedRoute / AppLayout
    } catch {
      // Invalid token, continue with normal flow
    }
  }

  if (!isAuthenticated && !isLoading) {
    if (isPublicSectorOverviewPath(location.pathname)) {
      return <SectorGuestView />;
    }
    return <Navigate to="/login" replace />;
  }

  if (isLoading || userDetails === undefined) {
    return <AuthRouteFallback />;
  }

  if (userDetails === null) {
    if (token) {
      invalidateClientSession();
    }
    return <Navigate to="/login" replace />;
  }

  const isComplete = isClientOnboardingComplete(userDetails);
  const status = userDetails.company?.status;
  const path = location.pathname;
  const profileCreated = hasClientCreatedProfile(userDetails);
  const flexDemoNeeded = needsFlexDemo(userDetails);
  const isFlexDemoPath = path === "/flex-demo" || path.startsWith("/flex-demo/");

  // Profile builder is one-time onboarding — never return completed users here.
  if (profileCreated && path === "/create-profile") {
    return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
  }

  if (!profileCreated) {
    if (path !== "/create-profile") {
      return <Navigate to="/create-profile" replace />;
    }
    return <>{children}</>;
  }

  if (flexDemoNeeded) {
    if (isFlexDemoPath || path === "/two-factor/setup") {
      return <>{children}</>;
    }
    return <Navigate to="/flex-demo" replace />;
  }

  if (isFlexDemoPath) {
    return <Navigate to="/my-discovery" replace />;
  }

  if (!isComplete) {
    if (status === "UNDER_REVIEW" && path === "/profile/edit") {
      sessionStorage.setItem(
        PROFILE_VERIFICATION_LANDING_KEY,
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
    }

    // Onboarding "incomplete" means the company isn't verified yet, but the user should still be able to
    // access their own account areas (profile/settings/collaboration wallet/edit profile) without being trapped on
    // the discovery page.
    const allowWhileUnverified =
      path === "/my-discovery" ||
      path.startsWith("/my-discovery/") ||
      path.startsWith("/ecosystem-heads/") ||
      path === MY_CLUSTER_PATH ||
      path.startsWith(`${MY_CLUSTER_PATH}/`) ||
      path.startsWith("/sector/") ||
      path === "/profile" ||
      path === "/profile/edit" ||
      path === "/settings" ||
      path === "/account" ||
      isCollaborationWalletPath(path) ||
      path === "/invoices" ||
      path === "/invoices/wallet" ||
      path === "/two-factor/setup" ||
      path === "/home" ||
      path === "/dashboard";

    if (!allowWhileUnverified) {
      return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
    }
  }

  return <>{children}</>;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { userDetails } = useUserContext();
  const location = useLocation();

  return (
    <ProtectedRouteGate
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
      userDetails={userDetails as UserWithCompany | null | undefined}
      location={location}
    >
      {/* Catch lazy AppLayout here so outer guest RouteFallback never flashes without the sidebar. */}
      <Suspense fallback={<AuthRouteFallback />}>{children}</Suspense>
    </ProtectedRouteGate>
  );
}
